// UI primitives — ported from design/components.jsx.
import type { ReactNode, CSSProperties } from "react";
import { Fragment, useEffect, useId, useRef } from "react";

import { I } from "./Icons";
import { fmtCcy, ccySym } from "@/utils/format";

// ─── Dialog a11y hook ─────────────────────────────────────────────────────
//
// Shared between Modal and Drawer. Closes the three open audit gaps:
//   1. Escape key  — no document-level listener was attached.
//   2. Focus trap  — Tab / Shift+Tab could escape the dialog into the
//                    page underneath, leaving the user keyboard-stranded.
//   3. Restore focus — the element that opened the dialog lost focus and
//                    keyboard users had to Tab from the page top after
//                    closing.
//
// `role="dialog"` + `aria-modal="true"` + `aria-labelledby` are wired on
// the dialog element itself by each caller (we return the titleId here).

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function useDialogA11y(
  open: boolean,
  onClose: () => void,
  dialogRef: React.RefObject<HTMLDivElement | null>,
): string {
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Effect 1: focus capture + restore. Depends only on `open` so a parent
  // re-render that hands us a new onClose reference does not re-trigger
  // focus capture (which would steal focus from inside the open dialog).
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    if (dialog) {
      const first = dialog.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (first ?? dialog).focus();
    }
    return () => {
      // Restore focus to whatever had it before the dialog opened.
      opener?.focus?.();
    };
  }, [open, dialogRef]);

  // Effect 2: keyboard listener. Esc closes; Tab cycles within the dialog.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusables = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && active === last) {
        first.focus();
        e.preventDefault();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, dialogRef]);

  return titleId;
}

// ─── Pill / StatusPill ────────────────────────────────────────────────────

export type PillTone = "" | "pos" | "warn" | "neg" | "info";

export function Pill({
  tone = "",
  dot = true,
  children,
}: {
  tone?: PillTone;
  dot?: boolean;
  children: ReactNode;
}) {
  return (
    <span className={"pill " + tone}>
      {dot && <span className="dot" />}
      {children}
    </span>
  );
}

const STATUS_MAP: Record<string, { tone: PillTone; label: string }> = {
  // Mock status values (phase 1 screens)
  completed: { tone: "pos", label: "Completed" },
  processing: { tone: "info", label: "Processing" },
  scheduled: { tone: "", label: "Scheduled" },
  failed: { tone: "neg", label: "Failed" },
  returned: { tone: "neg", label: "Returned" },
  review: { tone: "warn", label: "In review" },
  // Live payout status enum (phase 5+) — backend returns pending/success/failure
  success: { tone: "pos", label: "Success" },
  failure: { tone: "neg", label: "Failure" },
  // Shared status values used across screens
  active: { tone: "pos", label: "Active" },
  paused: { tone: "warn", label: "Paused" },
  onboarding: { tone: "info", label: "Onboarding" },
  // Backend merchant status enum (Phase 06.1) — active | suspended | closed
  suspended: { tone: "warn", label: "Suspended" },
  closed: { tone: "neg", label: "Closed" },
  // Per-merchant membership roles (Phase 06.1 — RBAC team surface)
  owner: { tone: "info", label: "Owner" },
  operator: { tone: "", label: "Operator" },
  viewer: { tone: "", label: "Viewer" },
  verified: { tone: "pos", label: "Verified" },
  pending: { tone: "warn", label: "Pending" },
  // Migration 019 — payouts now enter as needs_approval and stay there
  // until an admin clicks Approve/Reject. Distinct tone from "pending"
  // (which means "dispatched to provider, awaiting confirmation").
  needs_approval: { tone: "info", label: "Needs approval" },
  invited: { tone: "info", label: "Invited" },
  revoked: { tone: "neg", label: "Revoked" },
  retired: { tone: "", label: "Retired" },
  // Phase 6 — upload validation row statuses (06-UI-SPEC Step 2)
  valid: { tone: "pos", label: "Valid" },
  invalid: { tone: "neg", label: "Invalid" },
  skipped: { tone: "", label: "Skipped" },
  // Phase 6 — batch-level statuses for BatchDetail (06-UI-SPEC Surface 2)
  partial: { tone: "warn", label: "Partial" },
  complete: { tone: "pos", label: "Complete" },
  // Migration 050 — a bank rail an operator has switched off. "neg" rather
  // than "warn": a disabled rail is not degraded, it is refusing traffic, and
  // it should read as loudly as a failure on a page whose other rails are
  // green. Distinct from "pending", which on a rail means its credentials
  // have never verified.
  disabled: { tone: "neg", label: "Disabled" },
};

export function StatusPill({ status }: { status: string }) {
  const it = STATUS_MAP[status] || { tone: "" as PillTone, label: status };
  return <Pill tone={it.tone}>{it.label}</Pill>;
}

// ─── Amount ────────────────────────────────────────────────────────────────

export function Amount({
  value,
  ccy,
  big,
  muted,
  sign = false,
}: {
  value: number | null | undefined;
  ccy: string;
  big?: boolean;
  muted?: boolean;
  sign?: boolean;
}) {
  const style: CSSProperties = big ? { fontSize: 22, letterSpacing: "-0.01em" } : {};
  return (
    <span className={"num" + (muted ? " dim" : "")} style={style}>
      <span
        className="dim"
        style={{ marginRight: 4, fontSize: big ? 14 : "0.85em" }}
      >
        {ccySym(ccy)}
      </span>
      {sign && value != null && value > 0 ? "+" : ""}
      {fmtCcy(value, ccy)}
    </span>
  );
}

// ─── Avatar ────────────────────────────────────────────────────────────────

export function Avatar({
  name,
  size = 26,
  tone,
}: {
  name: string;
  size?: number;
  tone?: string;
}) {
  const initials = (name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <span
      className="avatar"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.42,
        background: tone || "var(--ink)",
      }}
    >
      {initials}
    </span>
  );
}

// ─── Spark / Sparkbar ─────────────────────────────────────────────────────

export function Spark({
  values,
  w = 100,
  h = 22,
  color = "currentColor",
  fill = true,
}: {
  values: number[];
  w?: number;
  h?: number;
  color?: string;
  fill?: boolean;
}) {
  if (!values?.length) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const dx = w / Math.max(1, values.length - 1);
  const norm = (v: number) =>
    max === min ? h / 2 : h - 2 - ((v - min) / (max - min)) * (h - 4);
  const pts = values.map((v, i) => `${(i * dx).toFixed(1)},${norm(v).toFixed(1)}`).join(" ");
  const area = `0,${h} ${pts} ${w},${h}`;
  return (
    <svg className="spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      {fill && <polygon points={area} fill={color} opacity={0.08} />}
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth={1.25}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Sparkbar removed in audit-pass-4 M-6 — no remaining callers after the
// dashboard cleanup in 266272d (Trend column on the merchants table was
// the last consumer). Spark above stays because Kpi optionally renders it.

// ─── Segmented ─────────────────────────────────────────────────────────────
//
// AUDIT-PASS-6 P-1. Single-select filter switch used on Payouts (status
// filter), BatchDetail (row status filter), and similar table-filter
// surfaces. The visual is a horizontal pill row, one option highlighted.
//
// ARIA semantics: this maps to the radiogroup/radio pattern, NOT tablist.
// The controls don't toggle disjoint tabpanels — they refine the same
// table inline — so radiogroup/radio is the semantically honest choice.
// Screen readers announce "radio button group, N of M, <label>, selected"
// which matches what the operator sees. The optional `label` prop wires
// aria-label on the group container; callers pass it when the visual
// context doesn't carry a heading.

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  /** Optional accessible name for the radiogroup (e.g. "Status filter"). */
  label?: string;
}) {
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map((o) => {
        const selected = value === o.value;
        return (
          <button
            key={o.value}
            className={selected ? "on" : ""}
            onClick={() => onChange(o.value)}
            type="button"
            role="radio"
            aria-checked={selected}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Modal / Drawer ───────────────────────────────────────────────────────

export function Modal({
  open,
  onClose,
  title,
  children,
  foot,
  wide,
  disableScrimClose,
  hideCloseButton,
  busy,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  foot?: ReactNode;
  wide?: boolean;
  /** CR-03/WR-05: prevent backdrop click from closing the modal (e.g. one-time secret reveal). */
  disableScrimClose?: boolean;
  /** WR-05: hide the X close button in the modal header (e.g. one-time secret reveal). */
  hideCloseButton?: boolean;
  /**
   * AUDIT-PASS-10 FE-M8: when true, the modal is in-flight and CANNOT be
   * closed by Escape, scrim click, or the X button. Callers thread their
   * `submitting` state here so a slow network can't race against an Escape
   * keystroke — previously the Cancel button was correctly disabled but
   * Escape/scrim/X were not, letting onClose fire mid-submit and leaving
   * stale `error` state for the next open.
   */
  busy?: boolean;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  // AUDIT-PASS-10 FE-M8: when busy, swallow Escape too. useDialogA11y reads
  // onClose through onCloseRef (set on every render), so passing a no-op
  // while busy effectively gates the Escape branch without modifying the
  // hook signature. Scrim + X button below honor `busy` independently.
  const effectiveOnClose = busy ? noopClose : onClose;
  const titleId = useDialogA11y(open, effectiveOnClose, dialogRef);
  if (!open) return null;
  return (
    <Fragment>
      {/* CR-03 + FE-M8: backdrop click is suppressed by disableScrimClose OR busy. */}
      <div
        className="scrim"
        aria-hidden="true"
        onClick={disableScrimClose || busy ? undefined : onClose}
      />
      <div
        ref={dialogRef}
        className={"modal" + (wide ? " wide" : "")}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="drawer-head">
          <h2 id={titleId}>{title}</h2>
          <div className="flex-1" />
          {/* WR-05 + FE-M8: X is hidden by hideCloseButton OR busy. */}
          {!hideCloseButton && !busy && (
            <button
              className="icon-btn"
              onClick={onClose}
              type="button"
              aria-label="Close"
            >
              <I.X />
            </button>
          )}
        </div>
        <div className="drawer-body">{children}</div>
        {foot && <div className="drawer-foot">{foot}</div>}
      </div>
    </Fragment>
  );
}

// noopClose is the placeholder onClose used by Modal while busy=true so the
// Escape-key branch in useDialogA11y has nothing to call. Defined at module
// scope so its identity is stable across renders (preventing a re-attach of
// the document keydown listener every render).
function noopClose() {}

export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  foot,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  foot?: ReactNode;
  wide?: boolean;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useDialogA11y(open, onClose, dialogRef);
  if (!open) return null;
  return (
    <Fragment>
      <div className="scrim" aria-hidden="true" onClick={onClose} />
      <div
        ref={dialogRef}
        className={"drawer" + (wide ? " wide" : "")}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="drawer-head">
          <div className="stack-2 flex-1">
            <h2 id={titleId}>{title}</h2>
            {subtitle && (
              <div className="dim" style={{ fontSize: 12 }}>
                {subtitle}
              </div>
            )}
          </div>
          <button
            className="icon-btn"
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            <I.X />
          </button>
        </div>
        <div className="drawer-body">{children}</div>
        {foot && <div className="drawer-foot">{foot}</div>}
      </div>
    </Fragment>
  );
}

// ─── Toast (display-only — toast state lives in the Strata shell) ─────────

export type ToastItem = {
  id: string;
  title: string;
  body?: string;
  /** Variant drives the announcement urgency for screen readers.
   *  - "success" / undefined: `aria-live=polite` — announce when idle.
   *  - "error":               `aria-live=assertive` — interrupt the current speech.
   */
  variant?: "success" | "error";
};

/**
 * ToastStack — accessible toast region.
 *
 * AUDIT-PASS-5 T2-B / UI CRITICAL #2: the container previously had no
 * `role` or `aria-live`, so screen-reader users got nothing when a
 * toast appeared (the most common path for surfacing async success or
 * failure messages). Per WAI-ARIA practices for status messages:
 *
 *   - `role="status"` + `aria-live="polite"` — announce when the
 *     reader is idle (default; safe for success messages).
 *   - `role="alert"`  + `aria-live="assertive"` — interrupt the
 *     current speech (used for error variants).
 *   - `aria-atomic="true"` — re-announce the whole toast on update,
 *     not just the diff, so screen readers don't trail off mid-toast.
 *
 * Each toast is rendered with its own role to let the reader pick the
 * right urgency per item rather than blanket-asserting the whole stack.
 */
export function ToastStack({ items }: { items: ToastItem[] }) {
  return (
    <div className="toast-wrap">
      {items.map((t) => {
        const isError = t.variant === "error";
        return (
          <div
            className="toast"
            key={t.id}
            role={isError ? "alert" : "status"}
            aria-live={isError ? "assertive" : "polite"}
            aria-atomic="true"
          >
            <I.Check aria-hidden="true" />
            <div className="flex-1">
              <div className="title">{t.title}</div>
              {t.body && <div className="body">{t.body}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── KPI card ─────────────────────────────────────────────────────────────

export function Kpi({
  k,
  v,
  ccy,
  delta,
  up,
  warn,
  spark,
  onClick,
  title,
}: {
  k: string;
  v: string | number;
  ccy?: string;
  delta?: string;
  up?: boolean;
  warn?: boolean;
  spark?: number[];
  /**
   * Makes the tile open the rows it counted.
   *
   * A dashboard number with no way through to its rows is a number the
   * operator has to go and re-derive by hand before they can act on it — or
   * trust without checking. When present, the tile becomes a real button:
   * keyboard-reachable, Enter/Space activated, and cursor-pointer, rather
   * than a div with a click handler that only a mouse can find.
   */
  onClick?: () => void;
  title?: string;
}) {
  const interactive = onClick != null;
  return (
    <div
      className={"kpi" + (interactive ? " kpi-clickable" : "")}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      title={title}
    >
      <div className="k">{k}</div>
      <div className="v">
        {ccy && <span className="ccy">{ccySym(ccy)}</span>}
        {v}
      </div>
      <div className="row gap-12" style={{ alignItems: "center", marginTop: 6 }}>
        <div className="d">
          <span
            className={up ? "up" : ""}
            style={warn ? { color: "var(--warn)" } : undefined}
          >
            {up && (
              <I.ArrowUp
                size={10}
                stroke={2}
                style={{ verticalAlign: -1, marginRight: 2 }}
              />
            )}
            {delta}
          </span>
        </div>
        <div className="flex-1" />
        {spark && <Spark values={spark} w={68} h={20} color="var(--ink-3)" />}
      </div>
    </div>
  );
}

export function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="row gap-6">
      <span style={{ width: 8, height: 2, background: color }} />
      <span className="dim">{label}</span>
    </span>
  );
}

// ─── EmptyState ───────────────────────────────────────────────────────────

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      {icon && <div className="icon">{icon}</div>}
      <h3>{title}</h3>
      {body && <p>{body}</p>}
      {action}
    </div>
  );
}
